import { useModalStore } from "../../../store/report/modalStore";
import { useDashboardMutations } from "../../../hooks/reports/mutations/useDashboardMutations";
import Button from "../../UI/Button";
import { DashboardPin } from "../../../models/pageModels/ReportDashboard";

interface DashboardHeaderProps {
  groupId: string;
  groupName?: string;
  pins: DashboardPin[];
  isRefreshing: boolean;
  isLoading: boolean;
  isReorderMode: boolean;
  isSaving: boolean;
  refreshStatus: string | null;
  lastRefreshedAt: string | null;
  refreshLimitReached: boolean;
  refreshLimitMessage: string | null;
  onEnterReorderMode: () => void;
  onCancelReorder: () => void;
  onConfirmReorder: () => void;
}

const DashboardHeader = ({
  groupId,
  groupName = "Dashboard",
  pins,
  isRefreshing,
  isLoading,
  isReorderMode,
  isSaving,
  refreshStatus,
  lastRefreshedAt,
  refreshLimitReached,
  refreshLimitMessage,
  onEnterReorderMode,
  onCancelReorder,
  onConfirmReorder,
}: DashboardHeaderProps) => {
  const openModal = useModalStore((s) => s.openModal);
  const { refreshMutation } = useDashboardMutations(groupId);

  const hasPins = pins.length > 0;

  const renderRefreshStatus = () => {
    if (isLoading || !lastRefreshedAt) return null;

    const formatted = new Date(lastRefreshedAt).toLocaleString();

    if (refreshStatus === "Success") {
      return (
        <span className="text-success small">
          <i className="ri-checkbox-circle-line margin-end-1" />
          Last refreshed {formatted}
        </span>
      );
    }
    if (refreshStatus === "Partial Success") {
      return (
        <span className="text-warning small">
          <i className="ri-error-warning-line margin-end-1" />
          Partial refresh — {formatted}
        </span>
      );
    }
    if (refreshStatus === "Failed") {
      return (
        <span className="text-danger small">
          <i className="ri-close-circle-line margin-end-1" />
          Refresh failed — {formatted}
        </span>
      );
    }

    return null;
  };

  return (
    <div className="dashboard-header">
      <div className="column-auto">
        <h1>{groupName?.charAt(0)?.toUpperCase() + groupName?.slice(1) || "Dashboard"}</h1>
        {renderRefreshStatus()}
        {refreshLimitReached && refreshLimitMessage && (
          <div className="text-sub small">
            {refreshLimitMessage}
          </div>
        )}
      </div>
      <div className="row">
        {!isReorderMode ? (
          <>
            <Button
              id="refresh-dashboard"
              text="Refresh All"
              className="primary outline margin-right-2"
              onClick={() => refreshMutation.mutate()}
              icon={<i className="ri-refresh-line margin-end-2" />}
              iconLocation="start"
              disabled={isRefreshing || isLoading || refreshLimitReached}
              loading={isRefreshing || isLoading}
            />
            <Button
              id="add-summary-btn"
              text="Add Summary"
              className="primary outline margin-right-2"
              onClick={() => openModal("addSummary", { reportGroupId: groupId })}
              icon={<i className="ri-add-line margin-end-2" />}
              iconLocation="start"
              disabled={isRefreshing || isLoading}
            />
            {hasPins && !isRefreshing && (
              <Button
                id="enter-reorder-mode"
                text="Reorder"
                className="primary outline margin-right-2"
                onClick={onEnterReorderMode}
                icon={<i className="ri-drag-move-fill margin-end-2" />}
                iconLocation="start"
                disabled={pins.length < 2}
              />
            )}
          </>
        ) : (
          <>
            <Button
              id="cancel-reorder"
              text="Cancel"
              className="secondary margin-right-2"
              onClick={onCancelReorder}
              disabled={isSaving}
            />
            <Button
              id="confirm-reorder"
              text="Save Order"
              className="primary"
              onClick={onConfirmReorder}
              icon={<i className="ri-check-line margin-end-2" />}
              iconLocation="start"
              loading={isSaving}
              disabled={isSaving}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardHeader;
